// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import type {
	ScheduledEventCreateRequest,
	ScheduledEventListResponse,
	ScheduledEventResponse,
	ScheduledEventUpdateRequest,
} from '@fluxer/schema/src/domains/guild/ScheduledEventSchemas';

const logger = new Logger('ScheduledEventCommands');

export async function fetchScheduledEvents(guildId: string, includePast = false): Promise<ScheduledEventListResponse> {
	try {
		const response = await http.get<ScheduledEventListResponse>(Endpoints.GUILD_SCHEDULED_EVENTS(guildId), {
			query: includePast ? {include_past: 'true'} : undefined,
		});
		return response.body;
	} catch (error) {
		logger.error(`Failed to fetch events for guild ${guildId}:`, error);
		throw error;
	}
}

export async function createScheduledEvent(
	guildId: string,
	data: ScheduledEventCreateRequest,
): Promise<ScheduledEventResponse> {
	try {
		const response = await http.post<ScheduledEventResponse>(Endpoints.GUILD_SCHEDULED_EVENTS(guildId), {body: data});
		return response.body;
	} catch (error) {
		logger.error(`Failed to create an event in guild ${guildId}:`, error);
		throw error;
	}
}

export async function updateScheduledEvent(
	guildId: string,
	eventId: string,
	data: ScheduledEventUpdateRequest,
): Promise<ScheduledEventResponse> {
	try {
		const response = await http.patch<ScheduledEventResponse>(Endpoints.GUILD_SCHEDULED_EVENT(guildId, eventId), {
			body: data,
		});
		return response.body;
	} catch (error) {
		logger.error(`Failed to update event ${eventId}:`, error);
		throw error;
	}
}

export async function deleteScheduledEvent(guildId: string, eventId: string): Promise<void> {
	try {
		await http.delete(Endpoints.GUILD_SCHEDULED_EVENT(guildId, eventId));
	} catch (error) {
		logger.error(`Failed to delete event ${eventId}:`, error);
		throw error;
	}
}

export async function setScheduledEventInterest(
	guildId: string,
	eventId: string,
	interested: boolean,
): Promise<ScheduledEventResponse> {
	try {
		const response = await http.put<ScheduledEventResponse>(
			Endpoints.GUILD_SCHEDULED_EVENT_INTEREST(guildId, eventId),
			{
				body: {interested},
			},
		);
		return response.body;
	} catch (error) {
		logger.error(`Failed to update interest in event ${eventId}:`, error);
		throw error;
	}
}
